// Global state and materials
import { LABELS, POLYGONS, VERTICES } from "../Global/variables";
import { COLORS } from "../Global/materials";

// Obstacle and panel utilities
import { deselectObstacle } from "../Obstacles/ObstacleFunctions";
import { removeSegmentDragControls, setSegmentDragControls } from "./DragControls";
import { deselectPanels } from "../Panels/PanelFunctions";
import { sceneManager } from "../SetUps/SceneManager";

/**
 * Handles the selection of a segment (polygon area).
 * Deselects previously selected items and highlights the new selection.
 *
 * @param {object} selectedIndices - Current selected segment/obstacle/panel index.
 * @param {number} index - Index of the segment to select.
 * @param {function} setSelectedIndices - Recoil setter for selected state.
 * @param {function} setPolygonDetails - Setter to update the selected polygon's UI/metadata.
 */
export function selectSegment(selectedIndices, index, setSelectedIndices, setPolygonDetails) {
    let prev = selectedIndices.segmentIndex;
    sceneManager.controls.enabled = true; // ensure that orbit controls are enabled

    // Deselect obstacle if switching segments and one is selected
    if (prev != null && selectedIndices.obstacleIndex != null) {
        let selectedObs = POLYGONS[prev].obstructions[selectedIndices.obstacleIndex];
        deselectObstacle(selectedObs);
    }

    // If the same segment is clicked again, deselect everything
    if (prev === index && prev != null) {
        deselectSegment(index);
        deselectPanels(selectedIndices.panelIndex, setSelectedIndices, selectedIndices);
        setSelectedIndices({ segmentIndex: null, obstacleIndex: null, panelIndex: null });
    }

    // Otherwise, highlight the new segment and set up its drag controls
    else if (setPolygonDetails != null) {
        if (prev !== null) deselectSegment(prev);
        segmentHighlight(index, {
            opacity: 0.2,
            color: COLORS.red,
            transparent: true,
            labelVisible: true,
            vertexOpacity: 0.6
        });

        setSelectedIndices({ segmentIndex: index, obstacleIndex: null, panelIndex: null });
        setSegmentDragControls(index, setPolygonDetails);
    }
}

/**
 * Deselect a polygon segment by resetting its visual appearance and controls.
 * 
 * @param {number} index - Segment index to deselect.
 */
export function deselectSegment(index) {
    segmentHighlight(index, {
        opacity: 0.05,
        color: COLORS.white,
        transparent: true,
        labelVisible: false,
        vertexOpacity: 0
    });

    removeSegmentDragControls();
}

/**
 * Show or hide all measurement labels on segments.
 * 
 * @param {boolean} showLabels - True to show, false to hide.
 */
export function toggleSegmentLabels(showLabels) {
    for (let i = 0; i < POLYGONS.length; i++) {
        let segment = POLYGONS[i];
        for (let edge of segment.edges) {
            LABELS.measurements[edge.id].visible = showLabels;
        }
    }
}

/**
 * Show only the labels on the currently selected segment.
 * 
 * @param {number} index - Index of the segment to show labels for.
 */
export function showSelectedSegmentLabel(index) {
    let segment = POLYGONS[index];
    for (let edge of segment.edges) {
        LABELS.measurements[edge.id].visible = true;
    }
}

/**
 * Helper function to visually highlight a polygon segment.
 * Updates material, label visibility, and vertex opacity.
 * 
 * @param {number} index - Segment index.
 * @param {object} options - Highlight styling options:
 *   - opacity: material transparency level
 *   - color: material color (hex)
 *   - transparent: boolean for transparency
 *   - labelVisible: whether measurement labels are shown
 *   - vertexOpacity: vertex point opacity
 */
function segmentHighlight(index, options) {
    const segment = POLYGONS[index];

    // Update material
    segment.shape.material.opacity = options.opacity || 0.2;
    segment.shape.material.color.setHex(options.color || 0xffffff);
    segment.shape.material.transparent = options.transparent || false;

    // Toggle edge label visibility
    for (let edge of segment.edges) {
        LABELS.measurements[edge.id].visible = options.labelVisible || false;
    }

    // Adjust vertex point visibility
    VERTICES[index].forEach(vertex => {
        vertex.material.opacity = options.vertexOpacity || 0;
    });
}
